// packages/core の battle / meta への窓口。画面はここ経由で core を使う。
// battle の関数は名前で引き直せるようにしてある（無ければ「準備中」を出す）。
import * as core from '@hyakki/core';
import {
  CHARS,
  GACHA,
  PRODUCTS,
  STAGES,
  buildLoadout,
  gachaRates as metaGachaRates,
  levelInfo as metaLevelInfo,
  newProfile,
  powerOf as metaPowerOf,
  recommendedPower as metaRecommendedPower,
  stageStaminaCost,
  type BattleLoadout,
  type CharId,
  type Command,
  type Profile,
  type StageInfo,
  type TargetRef,
} from '@hyakki/core';

const C = core as unknown as Record<string, unknown>;
function fnOf<F>(name: string): F | null {
  const v = C[name];
  return typeof v === 'function' ? (v as F) : null;
}

// ---------- battle ----------
export type Battle = core.Battle;
export type BattleEvent = core.BattleEvent;
export type ApplyResult = { ok: true; events: BattleEvent[] } | { ok: false; error: string };

export interface BattleApi {
  createBattle(setup: core.BattleSetup): Battle;
  advance(b: Battle): BattleEvent[];
  apply(b: Battle, cmd: Command): ApplyResult;
  autoCommand(b: Battle, opts?: core.AiOptions): Command;
  isOver(b: Battle): boolean;
  result(b: Battle): core.BattleResult;
  legalCommands(b: Battle): core.LegalCommands;
  reachableCells(b: Battle): core.ReachCell[];
  routeTo(b: Battle, cell: number): number[];
  validRoute(b: Battle, route: number[], partial?: boolean): boolean;
  attackTargets(b: Battle, skill?: boolean, ougi?: boolean): TargetRef[];
  forecast(b: Battle, target: TargetRef, skill?: boolean, attackerId?: number, ougi?: boolean): core.Forecast | null;
  buildableCells(b: Battle, type: core.BuildingType): number[];
  buildCheck(b: Battle, type: core.BuildingType): string | null;
  buildCostOf(b: Battle, type: core.BuildingType): number;
  buildCount(b: Battle): { built: number; max: number; turnUsed: number; turnMax: number };
  turnOrder(b: Battle, n?: number): { id: number; at: number }[];
  territoryShare(b: Battle, side: core.Side): number;
  territoryTier(b: Battle, side: core.Side): number;
  territoryRule(b: Battle): core.TerritoryRule;
  skillWait(b: Battle, id: number): number;
  castCells(b: Battle, ougi?: boolean): number[];
  castArea(b: Battle, ougi?: boolean, cell?: number): number[];
}

const BATTLE_FNS = [
  'createBattle',
  'advance',
  'apply',
  'autoCommand',
  'isOver',
  'result',
  'legalCommands',
  'reachableCells',
  'routeTo',
  'validRoute',
  'attackTargets',
  'forecast',
  'buildableCells',
  'buildCheck',
  'buildCostOf',
  'buildCount',
  'turnOrder',
  'territoryShare',
  'territoryTier',
  'territoryRule',
  'skillWait',
  'castCells',
  'castArea',
] as const;

/** battle の公開APIが揃っていれば返す。足りなければ null（画面は「準備中」） */
export function battleApi(): BattleApi | null {
  const out: Record<string, unknown> = {};
  for (const k of BATTLE_FNS) {
    const f = fnOf<(...a: unknown[]) => unknown>(k);
    if (!f) return null;
    out[k] = f;
  }
  return out as unknown as BattleApi;
}

// ---------- ステージ ----------
export const stages = (): StageInfo[] => STAGES;
export const stageById = (id: number) => STAGES[id - 1] ?? null;

/** 兵糧の消費（台帳は meta） */
export const hyoroCost = (stage: StageInfo, difficulty = 0) => stageStaminaCost(stage.id, difficulty);

// ---------- meta ----------
export const levelInfo = (exp: number) => metaLevelInfo(exp);
export const recommendedPower = (stageId: number, difficulty = 0) => metaRecommendedPower(stageId, difficulty);
export function powerOf(profile: Profile | null, charId: CharId): number | null {
  if (!profile) return null;
  try {
    return metaPowerOf(profile, charId);
  } catch {
    return null;
  }
}

/** 開発用：サーバーが無いときに端末だけで試合を作るための出撃性能 */
export function localLoadout(profile: Profile | null, charId: CharId): BattleLoadout {
  try {
    return buildLoadout(profile ?? newProfile(Date.now(), charId), charId);
  } catch {
    const t = fnOf<(c: CharId, tier?: number) => BattleLoadout>('testLoadout');
    if (t) return t(charId, 1);
    throw new Error('loadout');
  }
}

export const products = () => PRODUCTS;
export const gachaRates = () => metaGachaRates();
export const gachaCost = () => {
  const g = GACHA as unknown as { cost1?: number; cost10?: number };
  return { one: g.cost1 ?? 0, ten: g.cost10 ?? 0 };
};
export const charName = (id: CharId) => CHARS[id].name;
